import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ComputedCost } from '../../types';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 2 }).format(value);

const formatNumber = (value: number, decimals = 2) =>
  new Intl.NumberFormat('da-DK', { maximumFractionDigits: decimals }).format(value);

const ruleTypeLabels: Record<string, string> = {
  PER_KM: 'Per km',
  PER_DAY: 'Per dag',
  PER_TRIP: 'Per tur',
  PER_HOUR: 'Per time',
  PER_INVOICE_LINE: 'Per fakturalinje',
  FIXED_MONTHLY: 'Fast månedlig',
};

interface CostBreakdownTableProps {
  costs: ComputedCost[];
  totalCost: number;
}

export default function CostBreakdownTable({ costs, totalCost }: CostBreakdownTableProps) {
  if (costs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Ingen omkostningsregler matcher de valgte parametre
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Regel</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Sats</TableCell>
            <TableCell align="right">Enheder</TableCell>
            <TableCell align="right">Beløb</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {costs.map((cost) => (
            <TableRow key={cost.ruleId}>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {cost.ruleName}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {ruleTypeLabels[cost.ruleType] || cost.ruleType}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {formatCurrency(cost.rate)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(cost.units)} {cost.unitLabel}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={500}>
                  {formatCurrency(cost.amount)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider' } }}>
            <TableCell colSpan={4}>
              <Typography variant="subtitle2" fontWeight={600}>
                Total omkostning
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight={600}>
                {formatCurrency(totalCost)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
